import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Bookmark, MapPin, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';
import AddPlaceDialog from './AddPlaceDialog';

// Lavender theme tokens
const THEME = {
  primary: '#C4B5FD',
  light: '#EDE9FE',
  lighter: '#F5F3FF',
  dark: '#5B21B6',
  icon: '#8B5CF6',
  border: '#DDD6FE',
  surface: '#FAFAFD',
};

const PlacesTab = () => {
  const { isDemoMode } = useDemo();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchPlaces();
  }, [isDemoMode]);

  const fetchPlaces = async () => {
    // In demo mode, return empty places (no saved places in demo)
    if (isDemoMode) {
      setPlaces([]);
      setLoading(false);
      return;
    }
    
    try {
      const response = await API.get('/parking/places/all');
      setPlaces(response.data);
    } catch (error) {
      console.error('Failed to fetch saved places:', error);
      toast.error('Failed to load saved places');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    // In demo mode, just remove from state
    if (isDemoMode) {
      setPlaces(prev => prev.filter(p => p.id !== id));
      toast.success('Place deleted');
      return;
    }
    
    try {
      await API.delete(`/parking/places/${id}`);
      toast.success('Place deleted');
      fetchPlaces();
    } catch (error) {
      toast.error('Failed to delete place');
    }
  };

  const handleParkHere = async (place) => {
    try {
      const newParking = {
        title: `Parked at ${place.name}`,
        address: place.address,
        latitude: place.latitude,
        longitude: place.longitude,
        place_id: place.id,
        notes: place.default_notes,
        reminder_minutes_before_meter: place.default_reminder_meter,
        reminder_minutes_before_leave: place.default_reminder_leave,
      };
      await API.post('/parking', newParking);
      toast.success('Parking saved! Check Active tab.');
    } catch (error) {
      toast.error('Failed to create parking');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h3 
            className="text-xl font-semibold"
            style={{ color: THEME.dark }}
          >
            Saved Places
          </h3>
          <p className="text-sm text-gray-500 mt-1">Quick-save spots you park often</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
          style={{ 
            backgroundColor: THEME.primary,
            color: THEME.dark,
          }}
          data-testid="add-place-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Save Place
        </Button>
      </div>

      {places.length === 0 ? (
        <div 
          className="text-center py-16 rounded-2xl shadow-sm"
          style={{ 
            backgroundColor: THEME.lighter,
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{ backgroundColor: THEME.light }}
          >
            <Bookmark className="h-8 w-8" style={{ color: THEME.dark }} />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No saved places</h4>
          <p className="text-gray-500 max-w-sm mx-auto">
            Save places you park frequently for quick access
          </p>
          {/* Single CTA in header - no duplicate button here */}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {places.map((place) => (
            <Card 
              key={place.id} 
              className="overflow-hidden hover:shadow-lg transition-all duration-200"
              style={{ 
                borderColor: THEME.border,
                borderWidth: '1px',
                borderLeftWidth: '4px',
                borderLeftColor: THEME.primary,
              }}
            >
              <CardContent className="p-5">
                <div className="flex flex-col h-full">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: THEME.light }}
                      >
                        <Bookmark className="h-5 w-5" style={{ color: THEME.dark }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">{place.name}</h4>
                        {place.address && (
                          <div className="flex items-start gap-1.5 text-sm text-gray-500 mt-1">
                            <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-2">{place.address}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {place.default_notes && (
                      <p 
                        className="text-xs p-3 rounded-lg mb-3"
                        style={{ 
                          backgroundColor: THEME.lighter,
                          color: '#6B7280',
                        }}
                      >
                        {place.default_notes}
                      </p>
                    )}
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <Button
                      onClick={() => handleParkHere(place)}
                      className="flex-1 shadow-sm hover:shadow-md transition-shadow"
                      style={{ 
                        backgroundColor: THEME.primary,
                        color: THEME.dark,
                      }}
                      size="sm"
                      data-testid={`park-here-btn-${place.id}`}
                    >
                      Park Here
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(place.id)}
                      className="text-rose-400 hover:text-rose-600 hover:bg-rose-50"
                      data-testid={`delete-place-btn-${place.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AddPlaceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          fetchPlaces();
          setDialogOpen(false);
        }}
      />
    </>
  );
};

export default PlacesTab;
